import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../errors';
import type {
  DurationType,
  BillingCycle,
} from './createSubscriptionPlanApiService';

// ============================================================================
// API request payload (backend contract)
// ============================================================================

export interface EditSubscriptionPlanPayload {
  planName: string;
  planDescription: string;
  durationType: DurationType;
  duration: number;
  billingCycle: BillingCycle;
  price: number;
  limits?: {
    numberOfUsers?: number;
    numberOfFacilities?: number;
    numberOfQmsDocuments?: number;
  };
  offerId?: string | null;
}

// ============================================================================
// Input from form (UI values)
// ============================================================================

export interface EditSubscriptionPlanInput {
  planId: string;
  planName: string;
  planDescription: string;
  durationType: string; // "Miesięczny" | "Roczny"
  period: string; // e.g. "1 miesiąc", "12 miesiące", "1 rok", "5 lat"
  cycle: string; // "Miesięczny" | "kwartalny" | "Roczny"
  price: string; // may contain spaces as thousand separator
  userCount?: string;
  facilityCount?: string;
  qmsDocCount?: string;
  offerId?: string | null;
}

// ============================================================================
// Result
// ============================================================================

export interface EditSubscriptionPlanResult {
  success: boolean;
  data?: unknown;
  error?: string;
  /** Field-level errors from API validation (e.g. 400 with details[]). */
  fieldErrors?: Record<string, string>;
}

// ============================================================================
// Error messages
// ============================================================================

export const ERROR_MESSAGES = {
  DEFAULT_ERROR: SUBSCRIPTION_ERROR_MESSAGES.EDIT_PLAN_DEFAULT_ERROR,
  NETWORK_ERROR: SUBSCRIPTION_ERROR_MESSAGES.NETWORK_ERROR,
  NO_INTERNET_ERROR: SUBSCRIPTION_ERROR_MESSAGES.NO_INTERNET_ERROR,
  UNEXPECTED_ERROR: SUBSCRIPTION_ERROR_MESSAGES.UNEXPECTED_ERROR,
  PLAN_NAME_ALREADY_EXISTS:
    SUBSCRIPTION_ERROR_MESSAGES.PLAN_NAME_ALREADY_EXISTS,
  UNAUTHORIZED: SUBSCRIPTION_ERROR_MESSAGES.UNAUTHORIZED,
  VALIDATION_ERROR: SUBSCRIPTION_ERROR_MESSAGES.VALIDATION_ERROR,
  ZOD_VALIDATION: SUBSCRIPTION_ERROR_MESSAGES.ZOD_VALIDATION,
  INTERNAL_SERVER_ERROR: SUBSCRIPTION_ERROR_MESSAGES.INTERNAL_SERVER_ERROR,
} as const;

// ============================================================================
// Mappers: UI values → API enums / numbers (reused from create)
// ============================================================================

const mapDurationType = (durationType: string): DurationType => {
  return durationType === 'Roczny' ? 'YEARLY' : 'MONTHLY';
};

const PERIOD_TO_DURATION_MONTHLY: Record<string, number> = {
  '1 miesiąc': 1,
  '2 miesiące': 2,
  '3 miesiące': 3,
  '4 miesiące': 4,
  '5 miesiące': 5,
  '6 miesiące': 6,
  '7 miesiące': 7,
  '8 miesiące': 8,
  '9 miesiące': 9,
  '10 miesiące': 10,
  '11 miesiące': 11,
  '12 miesiące': 12,
};

const PERIOD_TO_DURATION_YEARLY: Record<string, number> = {
  '1 rok': 1,
  '2 lata': 2,
  '3 lata': 3,
  '4 lata': 4,
  '5 lat': 5,
  '6 lat': 6,
  '7 lat': 7,
  '8 lat': 8,
  '9 lat': 9,
  '10 lat': 10,
};

const getDuration = (durationType: string, period: string): number => {
  const map =
    durationType === 'Roczny'
      ? PERIOD_TO_DURATION_YEARLY
      : PERIOD_TO_DURATION_MONTHLY;
  const duration = map[period];
  if (duration === undefined) {
    return 1;
  }
  return duration;
};

const mapBillingCycle = (cycle: string): BillingCycle => {
  const normalized = (cycle || '').trim();
  if (normalized === 'kwartalny') {
    return 'QUARTERLY';
  }
  if (normalized === 'Roczny') {
    return 'YEARLY';
  }
  return 'MONTHLY';
};

/** Parse price string (may contain spaces) to number. */
const parsePrice = (price: string): number => {
  const cleaned = (price || '').replace(/\s/g, '').replace(',', '.');
  const value = parseFloat(cleaned);
  return Number.isNaN(value) ? 0 : value;
};

const parseIntOptional = (s: string | undefined): number | undefined => {
  if (s === undefined || s === '') {
    return undefined;
  }
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? undefined : n;
};

// ============================================================================
// Error extraction (map API status + body to user message)
// ============================================================================

interface ApiErrorData {
  message?: string;
  error?: string;
  statusCode?: number;
  code?: string;
  details?: Array<{ path?: string; message?: string; code?: string }>;
}

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401) {
    return SUBSCRIPTION_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 409) {
    return SUBSCRIPTION_ERROR_MESSAGES.CONFLICT_PLAN_NAME;
  }
  if (status === 500) {
    return SUBSCRIPTION_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    const msg = getMessageFromPayload(errorData);
    if (msg && msg.toLowerCase().includes('invalid json in request body')) {
      return SUBSCRIPTION_ERROR_MESSAGES.VALIDATION_ERROR;
    }
    // Prefer first detail message when available
    const fieldErrors = getFieldErrorsFromPayload(errorData);
    const firstDetailMessage =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? Object.values(fieldErrors)[0]
        : null;
    return (
      firstDetailMessage || msg || SUBSCRIPTION_ERROR_MESSAGES.ZOD_VALIDATION
    );
  }

  const msg = getMessageFromPayload(errorData);
  return msg || SUBSCRIPTION_ERROR_MESSAGES.EDIT_PLAN_DEFAULT_ERROR;
};

function getFieldErrorsFromPayload(
  errorData: unknown
): Record<string, string> | undefined {
  if (!errorData || typeof errorData !== 'object') {
    return undefined;
  }
  const obj = errorData as ApiErrorData;
  const details = obj.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }
  const fieldErrors: Record<string, string> = {};
  for (const d of details) {
    const path = d?.path;
    let message = d?.message;
    if (path && message) {
      if (
        path === 'price' &&
        (d?.code === 'too_big' || message.toLowerCase().includes('too big'))
      ) {
        message = SUBSCRIPTION_ERROR_MESSAGES.PRICE_TOO_BIG;
      }
      fieldErrors[path] = message;
    }
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

function getMessageFromPayload(errorData: unknown): string | null {
  if (!errorData) {
    return null;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData !== 'object') {
    return null;
  }

  const obj = errorData as ApiErrorData;
  const message = obj.message || obj.error;
  if (message) {
    return message;
  }

  // Zod often returns { errors: [...] }
  const errors = (errorData as { errors?: unknown[] }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object' && 'message' in first) {
      return String((first as { message: string }).message);
    }
  }

  return null;
}

// ============================================================================
// API call
// ============================================================================

const SUBSCRIPTIONS_ENDPOINT = 'loopay-subscription-management/subscriptions';

export const editSubscriptionPlan = async (
  input: EditSubscriptionPlanInput
): Promise<EditSubscriptionPlanResult> => {
  if (!input.planId?.trim()) {
    return {
      success: false,
      error: ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const durationType = mapDurationType(input.durationType);
    const duration = getDuration(input.durationType, input.period);
    const billingCycle = mapBillingCycle(input.cycle);
    const price = parsePrice(input.price);

    const limits: EditSubscriptionPlanPayload['limits'] = {};
    const users = parseIntOptional(input.userCount);
    const facilities = parseIntOptional(input.facilityCount);
    const qmsDocs = parseIntOptional(input.qmsDocCount);
    if (users !== undefined && users >= 0) {
      limits.numberOfUsers = users;
    }
    if (facilities !== undefined && facilities >= 0) {
      limits.numberOfFacilities = facilities;
    }
    if (qmsDocs !== undefined && qmsDocs >= 0) {
      limits.numberOfQmsDocuments = qmsDocs;
    }

    const payload: EditSubscriptionPlanPayload = {
      planName: input.planName.trim(),
      planDescription: (input.planDescription || '').trim(),
      durationType,
      duration,
      billingCycle,
      price,
      offerId: input.offerId || null,
    };

    if (Object.keys(limits).length > 0) {
      payload.limits = limits;
    }

    const result = await apiService.patchAPI(
      `${SUBSCRIPTIONS_ENDPOINT}/${input.planId.trim()}`,
      payload,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data,
      };
    }

    const errorMessage = extractErrorMessage(result.data, result.status);
    const fieldErrors = getFieldErrorsFromPayload(result.data);

    // Handle network errors (no internet connection)
    if (result.isNetworkError) {
      return {
        success: false,
        error: ERROR_MESSAGES.NO_INTERNET_ERROR,
      };
    }

    return {
      success: false,
      error: errorMessage,
      ...(fieldErrors && { fieldErrors }),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      // Handle network errors (no internet connection)
      if (isNetworkError) {
        return {
          success: false,
          error: ERROR_MESSAGES.NO_INTERNET_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData, status);
      const fieldErrors = getFieldErrorsFromPayload(errorData);
      return {
        success: false,
        error: errorMessage,
        ...(fieldErrors && { fieldErrors }),
      };
    }

    // Handle non-Axios errors (unknown errors)
    return {
      success: false,
      error: ERROR_MESSAGES.NETWORK_ERROR, // "Coś poszło nie tak. Spróbuj ponownie."
    };
  }
};
